/* Recover older carts whose extra units still share a complete-board line. */
(() => {
  'use strict';
  if (window.HalfpipedBoardQuantityRepair) return;
  const helper = () => window.HalfpipedBoardIntegrity;
  const quantities = () => window.HalfpipedBoardCartQuantity;
  const clone = value => JSON.parse(JSON.stringify(value));
  const memory = new Map();
  let running = null;
  const failure = message => Object.assign(new Error(message), { code: 'BOARD_QUANTITY_REPAIR_PENDING' });
  const storageKey = cart => cart?.token
    ? `hp-board-quantity-repair:v1:${window.Shopify?.shop || window.location.hostname}:${helper().sha256(String(cart.token))}` : null;
  const identity = item => helper().lineIdentity(item);
  const matches = (cart, id) => cart.items.filter(item => identity(item) === id);
  const one = (cart, id) => {
    const lines = matches(cart, id);
    if (lines.length > 1) throw failure('Several cart rows match this part. Please review your cart before separating the extra items.');
    return lines[0] || null;
  };
  const count = (cart, id) => matches(cart, id).reduce((sum, item) => sum + item.quantity, 0);
  const unconfirmed = (cart, target) => ['sending-add', 'changing'].includes(target.phase)
    && count(cart, target.identity) !== target.quantity;
  const cancelInstruction = journal => journal.sources.length === 1 ? 'remove the affected board' : 'remove all affected boards';
  const validJournal = value => value?.v === 1 && Array.isArray(value.sources) && value.sources.length > 0 && value.sources.length <= 50
    && value.sources.every(source => typeof source.identity === 'string' && source.identity.length <= 8192
      && Number.isSafeInteger(source.quantity) && source.quantity > 1 && source.quantity <= 100)
    && Array.isArray(value.destinations) && value.destinations.length > 0 && value.destinations.length <= 50
    && value.destinations.every(target => typeof target.identity === 'string' && target.identity.length <= 8192
      && /^[1-9]\d*$/.test(target.variantId) && target.properties && typeof target.properties === 'object' && !Array.isArray(target.properties)
      && identity({ variant_id: target.variantId, properties: target.properties }) === target.identity
      && !quantities().isBoardLine({ properties: target.properties })
      && Number.isSafeInteger(target.before) && target.before >= 0 && Number.isSafeInteger(target.quantity)
      && target.quantity > target.before && target.quantity <= 100
      && ['ready', 'sending-add', 'settled', 'changing', 'complete'].includes(target.phase));
  const read = cart => {
    const key = storageKey(cart);
    if (!key) return null;
    let raw;
    try { raw = window.localStorage?.getItem(key); }
    catch { return memory.get(key) || null; }
    // A successful missing read is authoritative: another tab may have finished
    // or cancelled the update. Memory is only a fallback when storage throws.
    if (!raw) { memory.delete(key); return null; }
    let value;
    try { value = JSON.parse(raw); } catch { throw failure('Your saved extra items need review. Please remove the affected custom board and add it again.'); }
    if (!validJournal(value)) throw failure('Your saved extra items need review. Please remove the affected custom board and add it again.');
    memory.set(key, value);
    return value;
  };
  const write = (cart, journal) => {
    const key = storageKey(cart);
    if (!key) throw failure('Please reload your cart before separating the extra items.');
    const raw = JSON.stringify(journal);
    // Repair has two Shopify writes. Persist recovery before either can change
    // the cart; memory alone cannot protect a reload between those writes.
    try {
      window.localStorage.setItem(key, raw);
      if (window.localStorage.getItem(key) !== raw) throw new Error('Storage did not retain the repair.');
    } catch { throw failure('Your browser could not save this cart update. Enable site storage, then select Checkout again to separate your extra items.'); }
    memory.set(key, clone(journal));
  };
  const clear = cart => {
    const key = storageKey(cart);
    if (!key) return;
    try { window.localStorage.removeItem(key); }
    catch { throw failure('Your extra items are in the cart, but the saved update could not be cleared. Please select Checkout again.'); }
    memory.delete(key);
  };
  const needsRepair = cart => (cart?.items || []).some(item => quantities()?.isBoardLine(item) && item.quantity > 1);
  const hasPending = cart => {
    try { return Boolean(read(cart)); }
    catch { return true; }
  };
  const progress = cart => {
    let journal;
    try { journal = read(cart); }
    catch { return { canKeepCurrent: false, message: 'Your saved extra items could not be verified. Empty your cart, then select Checkout to clear this saved update before rebuilding.' }; }
    if (!journal) return null;
    const requested = journal.destinations.reduce((sum, item) => sum + item.quantity - item.before, 0);
    const present = journal.destinations.reduce((sum, item) => sum + Math.max(0, Math.min(item.quantity, count(cart, item.identity)) - item.before), 0);
    const unknown = journal.destinations.some(item => unconfirmed(cart, item));
    const sourcesReduced = journal.sources.every(source => count(cart, source.identity) === 1);
    return {
      requested, present, unknown,
      canKeepCurrent: sourcesReduced && !unknown,
      message: unknown
        ? `We could not confirm the extra-item update. ${present} of ${requested} extra items are currently in your cart. Select Checkout to check again, or ${cancelInstruction(journal)} to cancel this update.`
        : `${present} of ${requested} extra items are currently in your cart. Select Checkout to finish separating them, or keep the quantities currently shown.`,
    };
  };
  const plan = cart => {
    const sources = [];
    const destinations = new Map();
    for (const item of cart.items) {
      if (!quantities().isBoardLine(item) || item.quantity <= 1) continue;
      const manifest = helper().manifestOf(item);
      const variantId = String(item.variant_id || item.id);
      if (!quantities().canAddExtra(item)) throw failure('A service or free board choice has an extra quantity. Remove the affected board from your cart and build it again to restore that choice to one.');
      if (!manifest || !manifest.parts.some(([part, id]) => part === item.properties._hp_board_bundle_component && id === variantId)
        || item.quantity > 100) throw failure('This saved board could not be verified. Please remove it and build it again.');
      const sourceId = identity(item);
      one(cart, sourceId);
      sources.push({ identity: sourceId, quantity: item.quantity });
      const properties = quantities().standaloneProperties(item.properties);
      const targetId = identity({ variant_id: variantId, properties });
      if (!destinations.has(targetId)) {
        const existing = one(cart, targetId);
        const before = existing?.quantity || 0;
        destinations.set(targetId, { identity: targetId, variantId, properties, before, quantity: before, phase: 'ready' });
      }
      destinations.get(targetId).quantity += item.quantity - 1;
    }
    const journal = { v: 1, sources, destinations: [...destinations.values()] };
    if (!validJournal(journal)) throw failure('There are too many extra items to separate safely. Please reduce their quantities in your cart.');
    return journal;
  };
  const candidateFor = (cart, journal) => {
    const candidate = clone(cart);
    for (const source of journal.sources) {
      const line = one(candidate, source.identity);
      if (!line || ![1, source.quantity].includes(line.quantity)) throw failure('The affected board changed while its extra items were being separated. Please review your cart.');
      line.quantity = 1;
    }
    for (const target of journal.destinations) {
      const line = one(candidate, target.identity);
      if (line && (line.quantity < target.before || line.quantity > target.quantity)) throw failure('An extra-item quantity changed during recovery. Please review your cart.');
      if (line) line.quantity = target.quantity;
      else if (target.before) throw failure('An existing extra item was removed while its quantity was being updated. Please review your cart.');
      else candidate.items.push({ variant_id: target.variantId, id: target.variantId, quantity: target.quantity, properties: target.properties });
    }
    return { ...candidate, integrityPreview: true };
  };
  const cancelRemovedSources = (cart, journal) => {
    const sources = journal.sources.filter(source => count(cart, source.identity));
    if (sources.length === journal.sources.length) return journal;
    // A request with no confirmed outcome can still arrive. Do not change its
    // target or guess which board its units belong to until the result is known.
    if (journal.destinations.some(target => unconfirmed(cart, target))) throw failure(progress(cart).message);
    const contributions = new Map();
    for (const source of sources) {
      const line = one(cart, source.identity);
      const targetId = identity({ variant_id: line.variant_id || line.id, properties: quantities().standaloneProperties(line.properties) });
      contributions.set(targetId, (contributions.get(targetId) || 0) + source.quantity - 1);
    }
    const revised = { ...clone(journal), sources: clone(sources), destinations: journal.destinations.map(target => {
      const actual = one(cart, target.identity)?.quantity || 0;
      if (actual < target.before || actual > target.quantity) throw failure('An extra-item quantity changed during recovery. Please review your cart.');
      // Removing a board cancels only its unfulfilled extras. Units already
      // confirmed as ordinary rows remain visible and are never removed here.
      const quantity = Math.max(target.before + (contributions.get(target.identity) || 0), actual);
      return { ...target, quantity, phase: actual === quantity ? 'complete' : 'settled' };
    }).filter(target => target.quantity > target.before) };
    if (!validJournal(revised)) throw failure('Your saved extra items need review. Please remove all affected boards and add them again.');
    return revised;
  };
  const preflight = async (cart, signal) => {
    const checkout = window.HalfpipedBoardCheckout;
    if (!checkout?.requestServer || !checkout?.config?.endpoint) throw failure('Please reload the page before separating the extra board parts.');
    const result = await checkout.requestServer('availability', cart, signal);
    if (result.removeBoards.length || result.blockingIssues.length) {
      throw failure('The requested extra items are not available in sufficient quantity, or your board needs review. Your board has not been removed.');
    }
  };
  const post = async (path, body, lockToken, signal, settled) => {
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal?.aborted) abort();
    else signal?.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 12000);
    try {
      const response = await window.fetch(`${window.Shopify?.routes?.root || '/'}cart/${path}.js`, {
        method: 'POST', credentials: 'same-origin', cache: 'no-store', hpCartLockToken: lockToken,
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: controller.signal,
      });
      // A 422 can have added some available stock. Once the response body finishes,
      // save that fact before reading the actual cart; status alone proves nothing
      // about the quantities written.
      await response.text();
      settled?.();
    } finally { clearTimeout(timer); signal?.removeEventListener('abort', abort); }
  };
  const publish = async (cart, onCart) => {
    const revision = window.HalfpipedCartMutations.getRevision();
    helper().prepare(cart);
    window.HalfpipedCartMutations.acknowledgeCart(revision);
    await onCart?.(cart, { confirmed: true, cartRevision: revision });
    const data = { source: 'board-quantity-repair', cartConfirmed: true, cartRevision: revision, itemCount: cart.item_count };
    document.dispatchEvent(new CustomEvent('cart:update', { detail: { resource: cart, sourceId: 'board-quantity-repair', data } }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart, data } }));
  };
  const repair = ({ cart, signal, onCart } = {}) => {
    if (running) return running;
    if (!needsRepair(cart) && !hasPending(cart)) return Promise.resolve(cart);
    running = (async () => {
      const mutations = window.HalfpipedCartMutations;
      await mutations.whenIdle({ signal, recoverFailureThroughRevision: mutations.getRevision() });
      let verified;
      try {
        verified = await helper().withCartLock(async lockToken => {
          let current = await helper().requestCart('cart.js', {}, signal);
          if (!current.items.length) { clear(current); return current; }
          let journal = read(current);
          // Removing the affected board cancels this intent, including an
          // unconfirmed request. Never replay an old add into a rebuilt board.
          // Any ordinary rows Shopify did confirm stay visible at normal prices.
          if (journal && journal.sources.every(source => !count(current, source.identity))) {
            clear(current);
            return current;
          }
          if (!journal && !needsRepair(current)) return current;
          if (!journal) journal = plan(current);
          journal = cancelRemovedSources(current, journal);
          for (const target of journal.destinations) {
            if (target.phase === 'sending-add' && count(current, target.identity) !== target.quantity) {
              throw failure(progress(current)?.message || 'The extra-item add is still unconfirmed. Select Checkout to check again.');
            }
          }
          const candidate = candidateFor(current, journal);
          await preflight(candidate, signal);
          write(current, journal);
          const finish = mutations.begin();
          try {
            // Reducing first never temporarily requires more stock than the
            // customer's original cart. The durable journal blocks checkout
            // until every requested unit has been accounted for.
            for (const source of journal.sources) {
              const line = one(current, source.identity);
              if (line.quantity === 1) continue;
              let error;
              try { await post('change', { id: line.key, quantity: 1 }, lockToken, signal); }
              catch (caught) { error = caught; }
              current = await helper().requestCart('cart.js', {}, signal);
              if (count(current, source.identity) !== 1) throw error || failure('The board quantity could not be updated. Select Checkout to retry safely.');
            }
            for (const target of journal.destinations) {
              const existing = one(current, target.identity);
              if ((existing?.quantity || 0) === target.quantity) { target.phase = 'complete'; write(current, journal); continue; }
              // Persist intent before an additive write so reload cannot blindly
              // repeat a request whose response was lost.
              target.phase = existing ? 'changing' : 'sending-add';
              write(current, journal);
              let error;
              try {
                await post(existing ? 'change' : 'add', existing
                  ? { id: existing.key, quantity: target.quantity }
                  : { items: [{ id: target.variantId, quantity: target.quantity, properties: target.properties }] },
                lockToken, signal, () => { target.phase = 'settled'; write(current, journal); });
              } catch (caught) { error = caught; }
              current = await helper().requestCart('cart.js', {}, signal);
              if (count(current, target.identity) !== target.quantity) {
                throw failure(progress(current)?.message || error?.message || 'The extra items could not all be added. Select Checkout to try again.');
              }
              target.phase = 'complete';
              write(current, journal);
            }
            for (const source of journal.sources) if (count(current, source.identity) !== 1) throw failure('Your board changed during the update. Please review your cart.');
            await preflight(current, signal);
            clear(current);
            return current;
          } finally { finish(); }
        }, signal);
      } catch (error) {
        // Publish only a fresh cart after the transaction lock has been released.
        // Failed repair stays journaled, so no background validation can clean up
        // the temporarily reduced board or silently check out fewer units.
        try {
          await mutations.whenIdle({ recoverFailureThroughRevision: mutations.getRevision() });
          const actual = await helper().requestCart();
          await publish(actual, onCart);
        } catch { /* Keep the original error and durable recovery intent. */ }
        throw error;
      }
      await mutations.whenIdle({ signal, recoverFailureThroughRevision: mutations.getRevision() });
      verified = await helper().requestCart('cart.js', {}, signal);
      await publish(verified, onCart);
      return verified;
    })().finally(() => { running = null; });
    return running;
  };
  const keepCurrent = async ({ cart, signal, onCart } = {}) => {
    if (running) throw failure('Please wait for the extra-item update to finish.');
    await window.HalfpipedCartMutations.whenIdle({ signal, recoverFailureThroughRevision: window.HalfpipedCartMutations.getRevision() });
    const current = await helper().withCartLock(async () => {
      const fresh = await helper().requestCart('cart.js', {}, signal);
      if (!progress(fresh)?.canKeepCurrent) throw failure('The extra-item add is still unconfirmed. Select Checkout to check again.');
      await preflight(fresh, signal);
      clear(fresh);
      return fresh;
    }, signal);
    await publish(current, onCart);
    return current;
  };
  const hasSavedWork = () => {
    const prefix = `hp-board-quantity-repair:v1:${window.Shopify?.shop || window.location.hostname}:`;
    try {
      for (let i = 0; i < window.localStorage.length; i++) {
        if (window.localStorage.key(i)?.startsWith(prefix)) return true;
      }
      return false;
    } catch { return memory.size > 0; }
  };
  const readAddItems = async (input, options) => {
    let body = options?.body;
    if (body === undefined && typeof input?.clone === 'function') {
      const request = input.clone();
      const contentType = request.headers.get('content-type') || '';
      body = /multipart\/form-data|application\/x-www-form-urlencoded/i.test(contentType)
        ? await request.formData() : await request.text();
    }
    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        return Array.isArray(parsed.items) ? parsed.items : parsed.id ? [parsed] : null;
      } catch { body = new URLSearchParams(body); }
    }
    if (!body || typeof body.entries !== 'function') return null;
    const entries = [...body.entries()];
    const item = { id: '', properties: {}, selling_plan: null };
    for (const [name, value] of entries) {
      if (name === 'id') item.id = String(value);
      else if (name === 'selling_plan') item.selling_plan = String(value);
      else if (/^properties\[.+\]$/.test(name)) {
        if (typeof value !== 'string') return null;
        item.properties[name.slice(11, -1)] = value;
      } else if (/^items\[/.test(name)) return null;
    }
    return item.id ? [item] : null;
  };
  const beforeCartMutation = async (input, options) => {
    const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, window.location.href);
    if (!/\/cart\/add(?:\.js)?\/?$/.test(url.pathname) || !hasSavedWork()) return;
    // This is called by the mutation wrapper while it owns the cart lock. Read
    // current durable state, never a preflight snapshot: a second tab may have
    // cancelled the operation or switched carts since the page loaded.
    const cart = await helper().requestCart();
    const journal = read(cart);
    if (!journal) return;
    if (journal.sources.every(source => !count(cart, source.identity))) { clear(cart); return; }
    const items = await readAddItems(input, options);
    if (!items || items.some(item => journal.destinations.some(target => target.identity === identity({
      variant_id: item.id, properties: item.properties || {}, selling_plan: item.selling_plan || null,
    })))) {
      throw Object.assign(failure(`Finish checking your saved extra items before adding another of the same item. Select Checkout, or ${cancelInstruction(journal)} to cancel that update.`), { cartWriteNotAttempted: true });
    }
  };
  window.HalfpipedBoardQuantityRepair = { needsRepair, hasPending, progress, repair, keepCurrent, beforeCartMutation, isPending: () => Boolean(running) };
})();
